import React, {ReactNode, useState} from "react";
import {css, keyframes} from "@emotion/react";
import styled from "@emotion/styled";

const ANIMATION_LENGTH = 500;
const TOOLTIP_WIDTH = 16; // rem

interface IProps extends ITooltipProps {
    children?: ReactNode;
    className?: string;
}

interface ITooltipProps {
    tooltipText: string | ReactNode;
    placement?: "top" | "bottom" | "bottom-left";
    enterDelay?: number;
    placementOffset?: string;
    last?: boolean;
    minWidth?: number;
}

export const SimpleTooltip = ({placement = "top", enterDelay = 500, ...props}: IProps) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <TooltipWrapper {...props} placement={placement} enterDelay={enterDelay}>
            {typeof props.tooltipText !== "string" && (
                <TooltipJSXContainer
                    onMouseEnter={() => setShowTooltip(true)}
                    onClick={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    minWidth={props.minWidth}
                >
                    {showTooltip && (
                        <TooltipJSXContent {...props} showTooltip={showTooltip} placement={placement} enterDelay={enterDelay}>
                            <div>{props.tooltipText}</div>
                        </TooltipJSXContent>
                    )}
                </TooltipJSXContainer>
            )}
            {props.children}
        </TooltipWrapper>
    );
};

const tooltipEnter = keyframes`
    0%{
        opacity: 0;
        max-height: 0;
    }
    50%{
        opacity: 1;
    }
    100%{
        opacity: 1;
        max-height: 200px;
    }
`;
interface ITooltipJSXContainer {
    children: ReactNode;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    minWidth?: number;
}

const TooltipJSXContainer = styled.div<ITooltipJSXContainer>`
    position: absolute;
    width: 100%;
    min-height: 3rem;
    z-index: 10;
`;

const TooltipJSXContent = styled.div<ITooltipProps & {showTooltip: boolean}>`
    position: absolute;
    overflow: hidden;
    height: auto;
    padding: 0.5rem 0.7rem;
    font-size: 1.2rem;
    z-index: 3000000;
    background-color: ${(props) => props.theme?.colors?.gray_dark || "#37474f"};
    color: #fff;
    text-align: center;
    border-radius: 0.6rem;
    transition:
        opacity 100ms,
        max-height ${ANIMATION_LENGTH / 300}s;
    opacity: 0;
    max-height: 0;
    pointer-events: none;
    animation: ${tooltipEnter} ${ANIMATION_LENGTH / 2000}s linear ${(props) => (props.enterDelay || 500) / 1000}s forwards;
    min-width: ${(props) => props.minWidth || TOOLTIP_WIDTH}rem;
    max-width: 100vw;

    ${(props) => {
        switch (props.placement) {
            case "top":
                return css`
                    bottom: calc(1.2rem + ${props.placementOffset || "10px"});
                    transform: translateX(-50%);
                    left: 50%;
                `;
            case "bottom":
                return css`
                    top: calc(1.2rem + ${props.placementOffset || "10px"});
                    transform: translateX(-50%);
                    left: 50%;
                `;
            case "bottom-left":
                return css`
                    top: calc(1.2rem + ${props.placementOffset || "10px"});
                    transform: translateX(0);
                    left: 0;
                `;
        }
    }}

    ${(props) =>
        props.showTooltip &&
        css`
            pointer-events: auto;
        `}
`;

const TooltipWrapper = styled.span<ITooltipProps>`
    position: relative;
    display: inline-block;

    &:hover:before {
        animation: ${tooltipEnter} ${ANIMATION_LENGTH / 1000}s linear ${(props) => (props.enterDelay || 500) / 1000}s;
        animation-fill-mode: forwards;
        height: auto;
        padding: 0.5rem 0.7rem;
        display: ${(props) => {
            return typeof props.tooltipText === "string" ? "block" : "none";
        }};
    }
    
    &:before {
        position: absolute;
        height: 0;
        padding: 0;
        width: ${TOOLTIP_WIDTH}rem;
        margin-left: -${TOOLTIP_WIDTH / 2}rem;
        left: 50%;
        font-size: 1.2rem;
        z-index: 3000000;
        opacity: 0;
        content: "${(props) => {
            return typeof props.tooltipText === "string" ? props.tooltipText : null;
        }}";
        display: none;
        transition: opacity ${ANIMATION_LENGTH / 1000}s, max-height ${ANIMATION_LENGTH / 300}s;
        background-color: ${(props) => props.theme?.colors?.gray_dark || "#37474f"};
        color: #fff;
        text-align: center;
        border-radius: 0.6rem;
        
    ${({last, theme}) =>
        last &&
        css`
            @media screen and (min-width: ${theme?.breakpoints_correct.screen_md || "1024px"}) and (max-width: 1260px) {
                left: unset;
                right: 0;
            }
        `}
        
        ${(props) => {
            if (props.placement === "top") {
                return css`
                    bottom: calc(1.2rem + ${props.placementOffset || "10px"});
                `;
            }
            if (props.placement === "bottom") {
                return css`
                    top: calc(1.2rem + ${props.placementOffset || "10px"});
                `;
            }
        }}
    `;
